import { type TranslationIDs, useIntl } from '@capsicum/common';
import { Alert, BaseCheckboxInput, BoldText, Text } from '@capsicum/common-web';
import { InfoIcon } from '@capsicum/common-web/icons';
import {
	useV2FTSupportedDestinationsWarningModalContent,
	type V2FTSupportedDestinationsWarningModalContentProps
} from '@capsicum/features/nc-wallet/shared';
import { useCallback } from 'react';
import styled from 'styled-components';

import { V2FTSupportedDestinations } from '../v2ft-supported-destinations/V2FTSupportedDestinations';

export const V2FTSupportedDestinationsWarningModalContent = ({
	asset,
	exchanges,
	wallets
}: V2FTSupportedDestinationsWarningModalContentProps) => {
	const { $t } = useIntl();
	const { isConfirmDisabled, toggleConfirmDisabled, v2SendWarningModalLink } =
		useV2FTSupportedDestinationsWarningModalContent();

	const translateContent = useCallback(
		(translationID: TranslationIDs) =>
			$t<JSX.Element>(translationID, {
				bold: chunks => <BoldText>{chunks}</BoldText>,
				destinationsListLink: chunks => (
					<S.LinkText
						data-testid="V2FTSupportedDestinationsWarningModalContent-destinationList-link"
						href={v2SendWarningModalLink}
						rel="noreferrer"
						target="_blank"
					>
						{chunks}
					</S.LinkText>
				),
				name: asset.symbol
			}),
		[$t, asset]
	);

	return (
		<S.Wrapper>
			<S.Content>
				<S.ContentText variant="title6">
					{translateContent('ncWallet.send.assetSelector.supportedDestinationsModal.title')}
				</S.ContentText>

				<S.ContentText variant="text3">
					{translateContent('ncWallet.send.assetSelector.supportedDestinationsModal.description')}
				</S.ContentText>
			</S.Content>

			{(wallets.length > 0 || exchanges.length > 0) && (
				<S.Destinations>
					<V2FTSupportedDestinations
						destinations={exchanges}
						testID="V2FTSupportedDestinationsWarningModal-exchanges-section"
						title={translateContent(
							'ncWallet.send.assetSelector.supportedDestinationsModal.sections.exchanges'
						)}
					/>

					<V2FTSupportedDestinations
						destinations={wallets}
						testID="V2FTSupportedDestinationsWarningModal-wallets-section"
						title={translateContent(
							'ncWallet.send.assetSelector.supportedDestinationsModal.sections.wallets'
						)}
					/>
				</S.Destinations>
			)}

			<Alert
				closable={false}
				icon={InfoIcon}
				subTitle={translateContent(
					'ncWallet.send.assetSelector.supportedDestinationsModal.alerts.additionalInfoWithLink'
				)}
				testID="V2FTSupportedDestinationsWarningModal-additionalInfo-alert"
				type="message"
			/>

			<BaseCheckboxInput
				testID="V2FTSupportedDestinationsWarningModal-confirm"
				value={!isConfirmDisabled}
				onChange={toggleConfirmDisabled}
			>
				{translateContent('ncWallet.send.assetSelector.supportedDestinationsModal.confirmation')}
			</BaseCheckboxInput>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.large};
	`;

	export const Content = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const ContentText = styled(Text)`
		text-align: center;
	`;

	export const Destinations = styled.div`
		display: flex;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const LinkText = styled.a`
		display: inline;
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;
}
